import React from 'react';
import { Crown, Users, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import Button from '../../../components/ui/Button';

const SubscriptionCard = ({ subscription, userProfile, hasActiveSubscription, onManageBilling, portalLoading }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': case'trialing':
        return 'text-green-600 bg-green-100';
      case 'past_due':
        return 'text-yellow-600 bg-yellow-100';
      case 'canceled': case'incomplete':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': case'trialing':
        return <CheckCircle className="w-5 h-5" />;
      case 'past_due':
        return <AlertTriangle className="w-5 h-5" />;
      case 'canceled': case'incomplete':
        return <XCircle className="w-5 h-5" />;
      default:
        return null;
    }
  };

  const tier = userProfile?.tier || 'free';
  const isPro = tier === 'pro' && hasActiveSubscription;

  return (
    <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-lg shadow-lg p-8 text-white">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          {isPro ? (
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Crown className="w-6 h-6" />
            </div>
          ) : (
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
          )}
          <div>
            <h2 className="text-2xl font-bold">{isPro ? 'Pro Plan' : 'Free Plan'}</h2>
            <p className="text-blue-100 text-sm">Current subscription</p>
          </div>
        </div>

        {subscription?.status && (
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription?.status)}`}>
            {getStatusIcon(subscription?.status)}
            <span className="capitalize">{subscription?.status}</span>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div>
          <p className="text-blue-100 text-sm mb-1">Spending Limit</p>
          <p className="text-2xl font-bold">${userProfile?.spending_limit?.toLocaleString() || '1,000'}</p>
        </div>
        <div>
          <p className="text-blue-100 text-sm mb-1">Monthly Swaps</p>
          <p className="text-2xl font-bold">{userProfile?.monthly_swap_limit || 10}</p>
        </div>
        <div>
          <p className="text-blue-100 text-sm mb-1">Transaction Fee</p>
          <p className="text-2xl font-bold">{isPro ? '2.4%' : '2.9%'}</p>
        </div>
      </div>

      {subscription?.currentPeriodStart && subscription?.currentPeriodEnd && (
        <div className="bg-white/10 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center text-sm">
            <div>
              <p className="text-blue-100 mb-1">Billing Period</p>
              <p className="font-semibold">
                {new Date(subscription?.currentPeriodStart)?.toLocaleDateString()} - {new Date(subscription?.currentPeriodEnd)?.toLocaleDateString()}
              </p>
            </div>
            {subscription?.cancelAtPeriodEnd && (
              <div className="text-right">
                <p className="text-yellow-200 font-semibold">Cancels on</p>
                <p className="text-sm">{new Date(subscription?.currentPeriodEnd)?.toLocaleDateString()}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {hasActiveSubscription && (
        <Button
          onClick={onManageBilling}
          disabled={portalLoading}
          className="w-full bg-white text-blue-600 hover:bg-blue-50 py-3 rounded-lg font-semibold transition-colors"
        >
          {portalLoading ? 'Loading...' : 'Manage Billing & Payment'}
        </Button>
      )}

      {subscription?.trialEnd && new Date(subscription?.trialEnd) > new Date() && (
        <div className="mt-4 text-center text-sm text-blue-100">
          Trial ends on {new Date(subscription?.trialEnd)?.toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

export default SubscriptionCard;